import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertMessageService } from '@shared/services/alert-message.service';
import { AuthService } from '@shared/services/auth.service';
import { BehaviorSubject, catchError, filter, from, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly alertMessageService = inject(AlertMessageService);

  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isApiUrl = request.url.startsWith(environment.apiBaseUrl);
    const authType = localStorage.getItem('auth_type');
    const isJwtAuth = authType === 'jwt';

    // Solo aplicar el interceptor a peticiones API con autenticación JWT.
    // Modo embed (token en memoria, sin cookie de refresh cross-site): el ciclo refresh→logout no
    // aplica. Un 401 se propaga y lo gestiona el componente, en vez de cerrar la sesión global del
    // iframe (que dispararía un logout espurio en medio del flujo).
    if (!isApiUrl || !isJwtAuth || this.authService.isEmbedSession) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si es un 401 y no es la petición de refresh token, intentar refrescar
        if (error.status === 401 && !request.url.includes('/auth/refresh-token') && !request.url.includes('/auth/logout')) {
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.authService.refreshToken()).pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(true);
          // Reintentar la petición original
          return next.handle(request);
        }),
        catchError(err => {
          this.isRefreshing = false;
          // Si el refresh también falla, redirigir al login
          this.alertMessageService.showError({ error: 'YourSessionHasExpiredPleaseLoginAgain', title: 'SessionExpired' });
          void this.router.navigate(['logoff']);
          return throwError(() => err);
        }),
      );
    } else {
      // Si ya se está refrescando, esperar a que termine
      return this.refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(() => next.handle(request)),
      );
    }
  }
}
